import api from './api.service';

// Types
export interface EstimationRequest {
  company_id?: string;
  estimation_name: string;
  customer_name: string;
  project_name?: string;
  bom_id: string;
  quantity: number;
  currency?: string;
  requested_date?: string;
  department_id?: string;
  opportunity_id?: string;
  description?: string;
}

export interface CostEstimation {
  id: string;
  estimationNumber: string;
  estimationName: string;
  customerName: string;
  projectName?: string;
  bomId: string;
  bomName: string;
  quantity: number;
  totalMaterialCost: number;
  totalLaborCost: number;
  totalOverheadCost: number;
  totalToolingCost: number;
  totalCost: number;
  unitCost: number;
  currency: string;
  estimatedLeadTimeDays: number;
  createdAt: Date;
}

export interface MarginRequest {
  total_cost: number;
  category?: string;
  customer_type?: string;
  customer_id?: string;
  quantity: number;
  order_value?: number;
  is_strategic?: boolean;
  complexity?: 'Low' | 'Medium' | 'High';
  min_margin?: number;
  max_margin?: number;
}

export interface ItemFeatures {
  materialType?: string;
  weight?: number;
  volume?: number;
  complexityScore?: number;
  numberOfOperations?: number;
  [key: string]: any;
}

class EstimationService {
  private baseURL = '';

  // Core Estimation CRUD
  async create(data: EstimationRequest) {
    const response = await api.post(`${this.baseURL}/estimations`, data);
    return response.data;
  }

  async getAll(filters?: any) {
    const response = await api.get(`${this.baseURL}/estimations`, { params: filters });
    return response.data;
  }

  async getById(id: string) {
    const response = await api.get(`${this.baseURL}/estimations/${id}`);
    return response.data;
  }

  async update(id: string, data: Partial<EstimationRequest>) {
    const response = await api.put(`${this.baseURL}/estimations/${id}`, data);
    return response.data;
  }

  async delete(id: string) {
    const response = await api.delete(`${this.baseURL}/estimations/${id}`);
    return response.data;
  }

  // Cost Calculation
  async calculateCost(bomId: string, quantity: number) {
    const response = await api.post(`${this.baseURL}/estimations/calculate`, {
      bom_id: bomId,
      quantity
    });
    return response.data;
  }

  async recalculate(id: string) {
    const response = await api.post(`${this.baseURL}/estimations/${id}/recalculate`);
    return response.data;
  }

  async makeVsBuyAnalysis(itemId: string, quantity: number) {
    const response = await api.post(`${this.baseURL}/estimations/make-vs-buy`, {
      item_id: itemId,
      quantity
    });
    return response.data;
  }

  // Version Management
  async getVersions(estimationId: string) {
    const response = await api.get(`${this.baseURL}/estimations/${estimationId}/versions`);
    return response.data;
  }

  async createVersion(estimationId: string, data: any) {
    const response = await api.post(`${this.baseURL}/estimations/${estimationId}/versions`, data);
    return response.data;
  }

  async compareVersions(version1Id: string, version2Id: string) {
    const response = await api.post(`${this.baseURL}/estimations/versions/compare`, {
      version1_id: version1Id,
      version2_id: version2Id
    });
    return response.data;
  }

  async createWhatIfScenario(estimationId: string, scenarioName: string, changes: any[]) {
    const response = await api.post(`${this.baseURL}/estimations/${estimationId}/what-if`, {
      scenario_name: scenarioName,
      changes
    });
    return response.data;
  }

  // Historical Analysis
  async getActualCosts(estimationId: string) {
    const response = await api.get(`${this.baseURL}/estimations/${estimationId}/actuals`);
    return response.data;
  }

  async recordActualCosts(estimationId: string, data: any) {
    const response = await api.post(`${this.baseURL}/estimations/${estimationId}/actuals`, data);
    return response.data;
  }

  async getVarianceAnalysis(estimationId: string) {
    const response = await api.get(`${this.baseURL}/estimations/${estimationId}/variance`);
    return response.data;
  }

  async getCostTrends(itemId: string, periodMonths: number = 12) {
    const response = await api.get(`${this.baseURL}/estimations/trends/${itemId}`, {
      params: { period_months: periodMonths }
    });
    return response.data;
  }

  async getAccuracyMetrics(estimatorId?: string) {
    const response = await api.get(`${this.baseURL}/estimations/accuracy`, {
      params: { estimator_id: estimatorId }
    });
    return response.data;
  }

  // Margin Optimization
  async calculateOptimalMargin(estimationId: string, request: MarginRequest) {
    const response = await api.post(`${this.baseURL}/estimations/${estimationId}/margin`, request);
    return response.data;
  }

  async getMarginScenarios(totalCost: number, targetRevenue?: number) {
    const response = await api.get(`${this.baseURL}/estimations/margin-scenarios`, {
      params: { total_cost: totalCost, target_revenue: targetRevenue }
    });
    return response.data;
  }

  async getMarginRules(isActive?: boolean) {
    const response = await api.get(`${this.baseURL}/margin-rules`, {
      params: { is_active: isActive }
    });
    return response.data;
  }

  async createMarginRule(data: any) {
    const response = await api.post(`${this.baseURL}/margin-rules`, data);
    return response.data;
  }

  async updateMarginRule(id: string, data: any) {
    const response = await api.put(`${this.baseURL}/margin-rules/${id}`, data);
    return response.data;
  }

  async deleteMarginRule(id: string) {
    const response = await api.delete(`${this.baseURL}/margin-rules/${id}`);
    return response.data;
  }

  // AI/ML Prediction
  async predictCost(features: ItemFeatures) {
    const response = await api.post(`${this.baseURL}/estimations/ai-predict`, { features });
    return response.data;
  }

  async trainModel() {
    const response = await api.post(`${this.baseURL}/estimations/ml/train`);
    return response.data;
  }

  async getModelAccuracy() {
    const response = await api.get(`${this.baseURL}/estimations/ml/accuracy`);
    return response.data;
  }

  async getPredictionHistory(limit: number = 50) {
    const response = await api.get(`${this.baseURL}/estimations/ml/history`, {
      params: { limit }
    });
    return response.data;
  }

  // Resource Planning
  async checkResourceAvailability(estimationId: string, requestedStartDate: string, estimation: any) {
    const response = await api.post(`${this.baseURL}/estimations/${estimationId}/check-capacity`, {
      requested_start_date: requestedStartDate,
      estimation
    });
    return response.data;
  }

  async estimateLeadTime(estimationId: string, requestedDate: string, estimation: any) {
    const response = await api.post(`${this.baseURL}/estimations/${estimationId}/lead-time`, {
      requested_date: requestedDate,
      estimation
    });
    return response.data;
  }

  async getResourceForecast(timeHorizonDays: number = 30) {
    const response = await api.get(`${this.baseURL}/estimations/resource-forecast`, {
      params: { time_horizon_days: timeHorizonDays }
    });
    return response.data;
  }

  // BOM Integration
  async getBOMCostStructure(bomId: string) {
    const response = await api.get(`${this.baseURL}/boms/${bomId}/cost-structure`);
    return response.data;
  }

  async calculateBOMCostRollup(bomId: string, quantity: number, includeScrap: boolean = true) {
    const response = await api.post(`${this.baseURL}/boms/${bomId}/cost-rollup`, {
      quantity,
      include_scrap: includeScrap
    });
    return response.data;
  }
}

export default new EstimationService();

